import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { toast } from 'react-hot-toast';
import {
  XMarkIcon,
  UserIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  CreditCardIcon,
  TruckIcon,
  ShoppingBagIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

/**
 * OrderDetailsModal - B2B Order Details Modal
 * מודל פרטי הזמנה ללקוח עסקי
 */
const OrderDetailsModal = ({ isOpen, onClose, order }) => {
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState(null);

  useEffect(() => {
    if (isOpen && order) {
      loadOrderData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, order]);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const loadOrderData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/orders/${order.id}`);
      setOrderData(response.data.order || response.data);
    } catch (error) {
      console.error('Error loading order:', error);
      toast.error('שגיאה בטעינת פרטי ההזמנה');
      // Use the order data we already have as fallback
      setOrderData(order);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'תאריך לא זמין';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('he-IL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'תאריך לא תקין';
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'pending': 'בהמתנה',
      'processing': 'בעיבוד',
      'confirmed': 'אושר',
      'shipped': 'נשלח',
      'in_transit': 'בדרך',
      'delivered': 'נמסר',
      'completed': 'הושלם',
      'cancelled': 'בוטל'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'shipped':
      case 'in_transit':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusText = (status) => {
    const statusMap = {
      'pending': 'ממתין לתשלום',
      'paid': 'שולם',
      'failed': 'נכשל',
      'refunded': 'הוחזר'
    };
    return statusMap[status] || status;
  };

  const getPaymentMethodText = (method) => {
    const methodMap = {
      'credit_card': 'כרטיס אשראי',
      'bank_transfer': 'העברה בנקאית',
      'company_credit': 'אשראי חברה',
      'cash': 'מזומן',
      'check': 'צ\'ק'
    };
    return methodMap[method] || method;
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-details-title"
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4" dir="rtl">
        <div className="relative w-full max-w-4xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-blue-50">
            <div>
              <h2 id="order-details-title" className="text-2xl font-bold text-gray-900">
                הזמנה #{orderData?.order_number || order?.order_number || '...'}
              </h2>
              <p className="text-gray-600 mt-1">
                נוצרה ב-{formatDate(orderData?.created_at || order?.created_at)}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(orderData?.status || order?.status)}`}>
                {getStatusText(orderData?.status || order?.status)}
              </span>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg p-1"
                aria-label="סגור חלון"
              >
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Loading State */}
            {loading && !orderData ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mb-4"></div>
                <p className="text-gray-600">טוען פרטי הזמנה...</p>
              </div>
            ) : !orderData && !order ? (
              <div className="flex flex-col items-center justify-center py-12">
                <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mb-4" />
                <p className="text-gray-900 font-bold text-lg">שגיאה בטעינת פרטי ההזמנה</p>
                <p className="text-gray-600 mt-2">אנא נסה שנית מאוחר יותר</p>
              </div>
            ) : (
              <div className="space-y-6">
                {(() => {
                  const data = orderData || order;
                  
                  return (
                    <>
                      {/* Customer & Order Info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Customer Info */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <UserIcon className="h-5 w-5 text-primary-600" />
                            פרטי לקוח
                          </h3>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                              <UserIcon className="h-4 w-4 text-gray-400" />
                              <span>{data.user ? `${data.user.first_name} ${data.user.last_name}` : 'לא זמין'}</span>
                            </div>
                            {data.user?.email && (
                              <div className="flex items-center gap-2">
                                <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                                <span>{data.user.email}</span>
                              </div>
                            )}
                            {data.user?.phone && (
                              <div className="flex items-center gap-2">
                                <PhoneIcon className="h-4 w-4 text-gray-400" />
                                <span>{data.user.phone}</span>
                              </div>
                            )}
                            {data.company && (
                              <div className="flex items-center gap-2">
                                <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                                <span>{data.company.name}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Payment & Shipping Info */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <CreditCardIcon className="h-5 w-5 text-primary-600" />
                            תשלום ומשלוח
                          </h3>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">אמצעי תשלום:</span>
                              <span className="font-medium">{getPaymentMethodText(data.payment_method)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">סטטוס תשלום:</span>
                              <span className={`font-medium ${data.payment_status === 'paid' ? 'text-green-600' : 'text-yellow-600'}`}>
                                {getPaymentStatusText(data.payment_status)}
                              </span>
                            </div>
                            {data.shipping_method && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">אופן משלוח:</span>
                                <span className="font-medium">{data.shipping_method}</span>
                              </div>
                            )}
                            {data.tracking_number && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">מספר מעקב:</span>
                                <span className="font-medium font-mono">{data.tracking_number}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Shipping Address */}
                      {(data.shipping_address || data.shippingAddress) && (() => {
                        const addr = data.shipping_address || data.shippingAddress || {};
                        const hasAddr = addr.street || addr.address_line_1 || addr.address_line1 || addr.city;
                        if (!hasAddr) return null;
                        return (
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                              <TruckIcon className="h-5 w-5 text-primary-600" />
                              כתובת משלוח
                            </h3>
                            <div className="space-y-1 text-sm">
                              {(addr.full_name || addr.first_name) && (
                                <p className="font-medium text-gray-900">
                                  {addr.full_name || `${addr.first_name || ''} ${addr.last_name || ''}`.trim()}
                                </p>
                              )}
                              {addr.company_name && (
                                <p className="text-gray-700">{addr.company_name}</p>
                              )}
                              <p className="text-gray-700">
                                {addr.street || addr.address_line_1 || addr.address_line1}
                                {addr.address_line_2 || addr.address_line2 ? `, ${addr.address_line_2 || addr.address_line2}` : ''}
                              </p>
                              {addr.city && (
                                <p className="text-gray-700">
                                  {addr.city}{addr.postal_code ? `, ${addr.postal_code}` : ''}
                                </p>
                              )}
                              {addr.phone && (
                                <div className="flex items-center gap-2 text-gray-700">
                                  <PhoneIcon className="h-4 w-4 text-gray-400" />
                                  <span>{addr.phone}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Order Items */}
                      <div>
                        <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <ShoppingBagIcon className="h-5 w-5 text-primary-600" />
                          פריטים בהזמנה ({data.items?.length || 0})
                        </h3>
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">מוצר</th>
                                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">מחיר יחידה</th>
                                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">כמות</th>
                                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">סה"כ</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {data.items?.map((item, index) => (
                                  <tr key={index}>
                                    <td className="px-4 py-3">
                                      <div className="flex items-center gap-3">
                                        {item.product?.images?.[0] && (
                                          <img 
                                            src={item.product.images[0]} 
                                            alt={item.product.name}
                                            className="w-12 h-12 object-cover rounded"
                                          />
                                        )}
                                        <div>
                                          <div className="font-medium text-gray-900">
                                            {item.product?.name || 'מוצר לא זמין'}
                                          </div>
                                          {item.product?.sku && (
                                            <div className="text-xs text-gray-500">SKU: {item.product.sku}</div>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-gray-900">{formatCurrency(item.unit_price)}</td>
                                    <td className="px-4 py-3 text-gray-900">{item.quantity}</td>
                                    <td className="px-4 py-3 font-medium text-gray-900">{formatCurrency(item.total_price)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>

                      {/* Order Totals */}
                      <div className="border-t border-gray-200 pt-6">
                        <div className="max-w-md mr-auto space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">סכום ביניים:</span>
                            <span className="font-medium">{formatCurrency(data.subtotal)}</span>
                          </div>
                          {data.discount_amount > 0 && (
                            <div className="flex justify-between text-sm text-green-600">
                              <span>הנחה:</span>
                              <span className="font-medium">-{formatCurrency(data.discount_amount)}</span>
                            </div>
                          )}
                          {data.shipping_amount > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">משלוח:</span>
                              <span className="font-medium">{formatCurrency(data.shipping_amount)}</span>
                            </div>
                          )}
                          {data.tax_amount > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">מע"מ:</span>
                              <span className="font-medium">{formatCurrency(data.tax_amount)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t">
                            <span>סה"כ לתשלום:</span>
                            <span className="text-primary-600">{formatCurrency(data.total_amount)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Notes */}
                      {data.notes && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <h4 className="font-bold text-gray-900 mb-2">הערות להזמנה:</h4>
                          <p className="text-sm text-gray-700">{data.notes}</p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
            >
              סגור
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;

